import React, { useEffect, useState } from "react";
import {
  Button,
  Dropdown,
  Radio,
  Collapse,
  RadioChangeEvent,
  Modal,
} from "antd";
import {
  KeyOutlined,
  MoreOutlined,
  StopOutlined,
  MessageFilled,
  PhoneFilled,
  DeleteOutlined,
} from "@ant-design/icons";
import { toast } from "react-toastify";
import { useThemeContext } from "../../../context/ThemeContext";
import { useSelector } from "react-redux";
import { customLogger } from "../../../utils/Logger";
import { RootState } from "../../../redux/store";
import { updateCurrentChat } from "../../../redux/slices/chatSlice";
import { useAppDispatch } from "../../../redux/utils/Dispatcher";
import { useDeleteUnansweredHandlingNumberMutation } from "../../../redux/services/settingsApi";
import ENDPOINTS from "../../../services/endpoint";
import apiInstance from "../../../services/apiService";
import EditModal from "../../../components/Settings/IncomingCalls/EditModal";
import AddNewModal from "../../../components/Settings/IncomingCalls/AddNewModal";
import SettingsSidebarHeader from "../../../components/common/SettingsSidebarHeader";
import GradientButton from "../../../components/common/UI/GradientButton";
import AddNewUnansweredModal from "../../../components/Settings/IncomingCalls/AddNewUnanswered";
import "./index.scss";

interface IncomingCallsProps {
  handleBackToPrevious: () => void;
}

interface DeviceList {
  deviceName: string;
  deviceType: number;
  ringDevice: number;
  udid: string;
  _id: string;
}

interface UsersNumbersList {
  isPrimary: boolean;
  isSeleted: boolean;
  phoneNumber: string;
  phoneId: string;
}

interface BlockedNumbersList {
  blockCallType: number;
  calls: boolean;
  createdDate: string;
  phoneNumber: string;
  sms: boolean;
  _id: string;
}

const IcomingCalls: React.FC<IncomingCallsProps> = ({
  handleBackToPrevious,
}) => {
  const dispatch = useAppDispatch();
  const [
    deleteUnansweredHandlingNumber,
    { isLoading: isForwardNumberloading },
  ] = useDeleteUnansweredHandlingNumberMutation();
  const { currentChat } = useSelector((state: RootState) => state.chat);
  const { theme } = useThemeContext();
  const [loading, setLoading] = useState(false);
  const [devicesList, setDevicesList] = useState<DeviceList[]>([]);
  const [blockedNumbers, setBlockedNumbers] = useState<BlockedNumbersList[]>(
    [],
  );
  const [numberOfRings, setNumberOfRings] = useState<string | number | null>(
    null,
  );
  const [usersNumbersList, setUsersNumberList] = useState<UsersNumbersList[]>(
    [],
  );

  const [addNewUnansweredOpen, setAddNewUnansweredOpen] =
    useState<boolean>(false);
  const [editModalOpen, setEditModalOpen] = useState<boolean>(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState<boolean>(false);
  const [addNewModalOpen, setAddNewModalOpen] = useState<boolean>(false);
  const [modalData, setModalData] = useState<BlockedNumbersList | null>(null);

  const getIconComponent = (iconName: string) => {
    switch (iconName) {
      case "ring":
        return (
          <img
            src={`/assets/icons/${theme}/ring.png`}
            width={20}
            height={20}
            alt="ring"
          />
        );
      case "laptop":
        return <img src="/assets/icons/e.svg" width={20} height={20} alt="" />;
      case "missed":
        return (
          <img
            src={`/assets/icons/${theme}/unanswered.png`}
            width={20}
            height={20}
            alt="unanswered"
          />
        );

      case "block":
        return (
          <img
            src={`/assets/icons/${theme}/block.png`}
            width={20}
            height={20}
            alt="block"
          />
        );
      default:
        return <KeyOutlined style={{ fontSize: "18px" }} />;
    }
  };

  const getCallsInfo = async () => {
    const url = ENDPOINTS.BLOCK_CONVERSATION;
    try {
      const response = await apiInstance.get(url);
      const data = response.data.Data;
      setNumberOfRings(data[0]?.noOfRings);
      setBlockedNumbers(data[0]?.blockedNumbers);
      setDevicesList(data[0]?.deviceList);
    } catch (error: any) {
      customLogger(error);
      toast.error(
        error.message ||
          "There was an error fetching calls info. Please try again",
      );
    }
  };

  const getUsersNumbersList = async () => {
    const url = ENDPOINTS.MY_NUMBERS;
    setLoading(true);
    try {
      const response = await apiInstance.get(url);
      const data = response.data.data;
      setUsersNumberList(data);
    } catch (error) {
      customLogger(error);
    } finally {
      setLoading(false);
    }
  };

  const handleModal = (number: any, type: string) => {
    setModalData(number);
    if (type === "edit") {
      setEditModalOpen(true);
    } else {
      setDeleteModalOpen(true);
    }
  };

  const customHeader = (title: string, icon: string, end: any) => (
    <div className="custom-header">
      <GradientButton className="option-icon">
        {getIconComponent(icon)}
      </GradientButton>
      <span>{title}</span>
      <span className="end-text">{end}</span>
    </div>
  );

  const handleRingDeviceChange = async (e: RadioChangeEvent) => {
    const value = e.target.value;
    if (!value) return;

    const url = ENDPOINTS.INCOMING_CALLS;
    setLoading(true);
    try {
      const response = await apiInstance.patch(url, {
        noOfRings: value,
      });
      if (response.status === 200) {
        toast.success("Incoming call setting updated successfully!");
        setNumberOfRings(value);
      }
    } catch (error) {
      customLogger(error);
      toast.error("Could not update setting. Please try again");
    } finally {
      setLoading(false);
    }
  };

  const handleUserNumberChange = async (e: RadioChangeEvent) => {
    const value = e.target.value;
    if (!value) return;

    const url = ENDPOINTS.INCOMING_CALLS;
    setLoading(true);
    try {
      const response = await apiInstance.patch(url, {
        forwardNumber: value,
      });
      if (response.status === 200) {
        toast.success("Incoming call setting updated successfully!");
        getUsersNumbersList();
      }
    } catch (error) {
      customLogger(error);
      toast.error("Could not update setting. Please try again");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    const url = ENDPOINTS.BLOCK_NUMBER + "/" + modalData?._id;

    setLoading(true);

    try {
      const response = await apiInstance.delete(url);
      if (response.status === 200) {
        toast.success("Phone number deleted Successfully");
        if (currentChat?.sourceNumber === modalData?.phoneNumber) {
          dispatch(
            updateCurrentChat({
              isBlocked: false,
            }),
          );
        }
        setDeleteModalOpen(false);
      }
    } catch (error) {
      customLogger(error);
      toast.error("An error occured. Please try again");
    } finally {
      setLoading(false);
      getCallsInfo();
    }
  };

  const handleDeleteForwardNumber = async (id: string) => {
    if (isForwardNumberloading || !id) return;
    try {
      await deleteUnansweredHandlingNumber({ id }).unwrap();
      getUsersNumbersList();
    } catch (err: any) {
      toast.error(err.data.message || "There was an error. Please try again.");
    }
  };

  useEffect(() => {
    getCallsInfo();
    getUsersNumbersList();
  }, []);

  return (
    <>
      <div className="incoming-calls-container">
        <SettingsSidebarHeader
          title="Incoming Calls"
          handleBackClick={handleBackToPrevious}
        />

        <div className="options-container">
          <Collapse
            expandIconPosition="end"
            className="custom-collapse"
            items={[
              {
                key: "3",
                label: customHeader(
                  "Unanswered Call handling",
                  "missed",
                  usersNumbersList.find((number) => number.isSeleted)
                    ?.phoneNumber || "",
                ),
                children: (
                  <>
                    <Radio.Group
                      disabled={loading}
                      onChange={(e) => handleUserNumberChange(e)}
                      className="device-list-radio-group"
                      value={
                        usersNumbersList.find((number) => number.isSeleted)
                          ?.phoneNumber
                      }
                    >
                      {usersNumbersList.map((number) => (
                        <Radio
                          className="custom-radio"
                          key={number?.phoneNumber}
                          value={number?.phoneNumber}
                        >
                          {number?.phoneNumber}
                          {number.phoneNumber.slice(0, 1) === "+" && (
                            <DeleteOutlined
                              style={{ color: "red" }}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteForwardNumber(number?.phoneId);
                              }}
                            />
                          )}
                        </Radio>
                      ))}
                    </Radio.Group>
                    <Button
                      className="add-new-btn"
                      onClick={() => setAddNewUnansweredOpen(true)}
                      type="text"
                    >
                      Add New...
                    </Button>
                  </>
                ),
              },
              {
                key: "4",
                label: customHeader(
                  "Blocked Numbers",
                  "block",
                  `${blockedNumbers.length} numbers` || "",
                ),
                children: (
                  <>
                    <div className="blocked-numbers-container">
                      {blockedNumbers.map((number) => (
                        <div
                          key={number.phoneNumber}
                          className="blocked-number-card"
                        >
                          <StopOutlined color="#EA3A5B" className="stop-icon" />
                          <div className="mid-wrapper">
                            <h2 className="number">{number.phoneNumber}</h2>
                            <div className="icons-wrapper">
                              {number.calls && (
                                <span className="icon blocked-icon">
                                  <PhoneFilled style={{ fontSize: "12px" }} />
                                </span>
                              )}
                              {number.sms && (
                                <span className="icon blocked-icon">
                                  <MessageFilled style={{ fontSize: "12px" }} />
                                </span>
                              )}
                            </div>
                          </div>
                          <Dropdown
                            overlayClassName="custom-dropdown"
                            className="menu-dropdown"
                            menu={{
                              items: [
                                {
                                  key: "1",
                                  label: (
                                    <span
                                      style={{ width: "100%" }}
                                      onClick={() =>
                                        handleModal(number, "edit")
                                      }
                                    >
                                      Edit
                                    </span>
                                  ),
                                },
                                {
                                  key: "2",
                                  label: (
                                    <span
                                      onClick={() =>
                                        handleModal(number, "delete")
                                      }
                                    >
                                      Delete
                                    </span>
                                  ),
                                },
                              ],
                            }}
                          >
                            <Button
                              type="text"
                              className="more-btn"
                              icon={<MoreOutlined />}
                            />
                          </Dropdown>
                        </div>
                      ))}
                    </div>

                    <Button
                      className="add-new-btn"
                      onClick={() => setAddNewModalOpen(true)}
                      type="text"
                    >
                      Add New...
                    </Button>
                  </>
                ),
              },
            ]}
          />
        </div>
      </div>

      <EditModal
        visible={editModalOpen}
        onClose={() => setEditModalOpen(false)}
        onAfterClose={getCallsInfo}
        data={modalData}
      />

      <Modal
        open={deleteModalOpen}
        onCancel={() => setDeleteModalOpen(false)}
        onOk={handleDelete}
        centered
        title="Confirm Deletion"
        okText="Yes"
        cancelText="No"
        okButtonProps={{
          loading: loading,
          style: {
            background:
              "linear-gradient(rgb(196, 53, 200) 34.17%, rgb(94, 104, 240) 100%)",
            border: "none",
          },
        }}
        cancelButtonProps={{
          disabled: loading,
          type: "text",
        }}
        className="custom-modal"
      >
        <p>Are you sure you want to delete this blocked number?</p>
      </Modal>

      <AddNewModal
        visible={addNewModalOpen}
        onClose={() => setAddNewModalOpen(false)}
        onAfterClose={getCallsInfo}
      />

      <AddNewUnansweredModal
        visible={addNewUnansweredOpen}
        onClose={() => setAddNewUnansweredOpen(false)}
        onAfterClose={getUsersNumbersList}
      />
    </>
  );
};

export default IcomingCalls;
